// EVV Feed — the office's window onto the state-aggregator transmitter.
//
// Shows whether the feed is armed (a line set to transmit AND a real
// transport configured), lets a coordinator build a batch on demand, and
// lists every batch with its records so a rejected file can be traced back
// to the visit. In dry-run mode (the default while AHFC is exempt) batches
// are built and stored exactly as they would be sent, and nothing leaves.
import { useCallback, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import DataTable from '../../components/DataTable';
import { getEvvFeedStatus, runEvvFeed, getEvvBatches, getEvvTransmissions } from '../../lib/scheduling';

const dt = (s) => s ? format(parseISO(s), 'd MMM yyyy · h:mmaaa') : '—';
const errBox = { background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 7, padding: '10px 14px', margin: '12px 0', fontSize: 12, color: 'var(--danger)' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', overflowY: 'auto' };
const sheet = { background: 'var(--card-bg)', borderRadius: 12, width: '100%', maxWidth: 900, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };

const BATCH_BADGE = { dry_run: 'badge-low', built: 'badge-normal', sent: 'badge-normal', accepted: 'badge-done', rejected: 'badge-urgent', error: 'badge-urgent' };
const TX_BADGE = { pending: 'badge-low', accepted: 'badge-done', rejected: 'badge-urgent', error: 'badge-urgent' };

export default function EvvFeed() {
  const [status, setStatus] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [records, setRecords] = useState([]);
  const [showLine, setShowLine] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [st, b] = await Promise.all([getEvvFeedStatus(), getEvvBatches()]);
    if (st.error) setError(st.error.message || String(st.error)); else setError('');
    setStatus(st.data || null);
    setBatches(b.data || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true); setError('');
    const { data, error: err } = await runEvvFeed();
    setBusy(false);
    if (err || data?.error) { setError(err?.message || data?.error); return; }
    window.alert(data?.skipped ? `Nothing built: ${data.skipped}.` : `${data.built} record${data.built === 1 ? '' : 's'} built for ${data.visits} visit${data.visits === 1 ? '' : 's'} (${data.transport}).`);
    load();
  };

  const openBatch = async (b) => {
    setOpen(b);
    const { data } = await getEvvTransmissions(b.id);
    setRecords(data || []);
  };

  const columns = [
    { accessorKey: 'created_at', header: 'Built', size: 170, meta: { mobile: 'primary' }, cell: ({ getValue }) => dt(getValue()) },
    { accessorKey: 'file_name', header: 'File', size: 260, meta: { mono: true, truncate: true } },
    { accessorKey: 'record_count', header: 'Records', size: 90, meta: { align: 'right' } },
    { accessorKey: 'transport', header: 'Transport', size: 100, meta: { mono: true } },
    { accessorKey: 'status', header: 'Status', size: 110, cell: ({ getValue }) => <span className={`badge ${BATCH_BADGE[getValue()] || 'badge-low'}`}>{String(getValue()).replace('_', ' ')}</span> },
    { id: 'by', header: 'By', size: 140, accessorFn: b => b.created_by_profile?.full_name || 'Scheduled run', cell: ({ row }) => row.original.created_by_profile?.full_name || <span style={{ color: 'var(--text3)' }}>Scheduled run</span> },
    { accessorKey: 'http_status', header: 'HTTP', size: 70, meta: { mono: true, mobile: 'hidden' }, cell: ({ getValue }) => getValue() ?? '—' },
  ];

  const armed = status?.armed;

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: 'var(--text3)', maxWidth: 600, lineHeight: 1.6 }}>
          Builds Georgia EVV records for Netsmart Mobile Caregiver+ from verified visits on any service line set to transmit,
          every 15 minutes and on demand. Field order and typecodes are reconciled during Netsmart UAT before the first real file.
        </div>
        <button className="btn btn-primary" onClick={run} disabled={busy || loading}>{busy ? 'Building…' : '▶ Build batch now'}</button>
      </div>

      {error && <div style={errBox}>⚠ {error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 1, background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
        {[
          ['Feed', armed ? 'ARMED' : 'Dry run', armed ? 'var(--danger)' : 'var(--text)'],
          ['Transport', status?.transport || '—', 'var(--text)'],
          ['Vendor source id', status?.sourceId || '—', 'var(--text)'],
          ['Endpoint', status ? (status.endpointConfigured ? 'Configured' : 'Not set') : '—', status?.endpointConfigured ? 'var(--success)' : 'var(--text3)'],
          ['Lines transmitting', status ? (status.transmitLines?.length ? status.transmitLines.join(', ') : 'none') : '—', status?.transmitLines?.length ? 'var(--warn)' : 'var(--text)'],
          ['Provider NPI', status?.provider?.npi || '—', 'var(--text)'],
          ['Medicaid provider id', status?.provider?.medicaidId || 'not in General Info', status?.provider?.medicaidId ? 'var(--text)' : 'var(--danger)'],
        ].map(([l, v, c]) => (
          <div key={l} style={{ background: 'var(--card-bg)', padding: '10px 14px' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: c, fontFamily: 'var(--mono)', wordBreak: 'break-word' }}>{v}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{l}</div>
          </div>
        ))}
      </div>

      {!armed && status && (
        <div style={{ fontSize: 12, color: 'var(--text2)', background: 'var(--bg2)', border: '0.5px solid var(--border)', borderRadius: 7, padding: '8px 12px', marginBottom: 14 }}>
          Nothing leaves this system while the feed is in dry run. To arm it: set <code>EVV_TRANSPORT</code>, <code>EVV_API_URL</code>, <code>EVV_API_USER</code>, <code>EVV_API_PASS</code> and <code>EVV_SOURCE_ID</code> as function secrets,
          then switch "EVV required" and "Transmit" on for a service line in the Service Catalog.
        </div>
      )}

      <div className="card">
        <DataTable tableId="evv-batches" columns={columns} data={batches} getRowId={b => b.id} onRowClick={openBatch} loading={loading}
          emptyMessage="No batches yet. Build one to see what would be sent." />
      </div>

      {open && (
        <div style={overlay}>
          <div style={sheet}>
            <div style={{ padding: '14px 20px', borderBottom: '0.5px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{open.file_name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{open.record_count} records · {open.transport} · {open.status}{open.http_status ? ` · HTTP ${open.http_status}` : ''}</div>
              </div>
              <button onClick={() => { setOpen(null); setShowLine(null); }} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text3)', lineHeight: 1 }} aria-label="Close">×</button>
            </div>
            <div style={{ padding: 20 }}>
              {records.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>Loading records…</div> : records.map(r => (
                <div key={r.id} style={{ borderBottom: '0.5px solid var(--border)', padding: '8px 0' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, fontSize: 12 }}>
                    <span style={{ fontFamily: 'var(--mono)' }}>{r.payload?.VisitId}</span>
                    <span style={{ fontWeight: 500 }}>{r.visit?.client ? `${r.visit.client.last_name}, ${r.visit.client.first_name} · ${r.visit.client.client_id}` : ''}</span>
                    <span className="badge badge-normal">{r.evv_status_sent}</span>
                    {r.split_part && <span className="badge badge-low">part {r.split_part}</span>}
                    <span className={`badge ${TX_BADGE[r.response_status] || 'badge-low'}`}>{r.response_status}</span>
                    <span style={{ color: 'var(--text3)' }}>{r.payload?.ServiceCode}{r.payload?.Modifier1 ? `-${r.payload.Modifier1}` : ''} · {r.payload?.InvoiceUnits || '0'} units · {r.payload?.StartVerificationType || '—'}/{r.payload?.EndVerificationType || '—'}</span>
                    <button className="btn" style={{ padding: '2px 8px', fontSize: 11, marginLeft: 'auto' }} onClick={() => setShowLine(showLine === r.id ? null : r.id)}>{showLine === r.id ? 'Hide' : 'Record'}</button>
                  </div>
                  {r.response_message && <div style={{ fontSize: 11, color: r.response_status === 'pending' ? 'var(--text3)' : 'var(--danger)', marginTop: 4 }}>{r.response_message}</div>}
                  {showLine === r.id && (
                    <pre style={{ marginTop: 8, fontSize: 11, fontFamily: 'var(--mono)', background: 'var(--bg2)', padding: 10, borderRadius: 6, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {Object.entries(r.payload || {}).filter(([, v]) => v !== '').map(([k, v]) => `${k}: ${v}`).join('\n')}
                    </pre>
                  )}
                </div>
              ))}
              {open.response_body && (
                <details style={{ marginTop: 12, fontSize: 12 }}>
                  <summary style={{ cursor: 'pointer', color: 'var(--text2)' }}>Aggregator response</summary>
                  <pre style={{ fontSize: 11, fontFamily: 'var(--mono)', background: 'var(--bg2)', padding: 10, borderRadius: 6, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{open.response_body}</pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
